import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
} from "@mui/material";
import DashboardHeader from "@components/Header/DashboardHeader";
import GenericBreadCrums from "@components/Breadcrumbs";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CallIcon from "@mui/icons-material/Call";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BusinessIcon from "@mui/icons-material/Business";
import BadgeIcon from "@mui/icons-material/Badge";
import CategoryIcon from "@mui/icons-material/Category";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import EditIcon from "@mui/icons-material/Edit";
import { RootState, AppDispatch } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import ApartmentIcon from "@mui/icons-material/Apartment";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import Groups3Icon from "@mui/icons-material/Groups3";
import DomainIcon from "@mui/icons-material/Domain";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import {
  fetchGSTDetailsThunk,
  fetchProfile,
  submitUserRegistrationDetailsThunk,
} from "./redux/thunk/profileThunk";
import { useFormik } from "formik";
import { SubmitValidationSchema } from "./formikSchema";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { StyledTextField } from "@components/CustomInput/customInput";
import EmailVerificationModal from "@components/Modal/EmailVerificationModal";
import { sendEmailOTP } from "@modules/login/services/apiEndpoints";
import {
  sendEmailOtpThunk,
  verifyEmailOtpThunk,
} from "@modules/login/redux/thunk/loginThunk";
import { Verified } from "@mui/icons-material";
import { setGstNumber } from "@modules/signup/redux/userRegistrationSlice";

const ViewProfile = () => {
  const breadcrumbData = [{ label: "My Profile", link: "#" }];
  const [activeEditFields, setActiveEditFields] = useState<string[]>([]);
  const [isEditingGST, setIsEditingGST] = useState(false);
  const [searchedGST, setSearchedGST] = useState("");

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { profile, loading, error } = useSelector(
    (state: RootState) => state.profile,
  );

  const selectedVals = (field: string) => {
    if (field === "GST") {
      return formik.values.gstNumber;
    }
    if (field === "email") {
      return formik.values.email;
    }
    if (field === "CIN") {
      return formik.values.cin;
    }
    if (field === "TIN") {
      return formik.values.tin;
    }
  };

  function getFirstAndLastInitial(name: string): string {
    if (!name) return "";
    const words = name.trim().split(/\s+/); // split by spaces
    const first = words[0]?.[0]?.toUpperCase() || "";
    const last =
      words.length > 1 ? words[words.length - 1][0].toUpperCase() : "";

    return first + last;
  }

  const {
    id,
    name,
    email,
    mobile_number,
    dob,
    address,
    user_category,
    organizations,
  } = profile || {
    id: "",
    name: "",
    email: "",
    mobile_number: "",
    dob: "",
    pan_number: "",
    address: "",
    organizations: [],
  };

  const handleEditField = (field: string) => {
    setActiveEditFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field],
    );
  };

  const handleDeleteField = (field: string) => {
    // clear the field value
    if (field === "GST") {
      setSearchedGST("");
      setGstNumber("");
      formik.setFieldValue("gstNumber", "");
      setInitialFormValues({
        ...formik.values, // keep existing formik values

        gstNumber: "",
        cin: "",
        tin: "",
        business_name: "",
        entity_type: "",
        business_nature: "",
        address: "",
        city_name: "",
        district_name: "",
        state_name: "",
        pincode: "",
        registration_date: "",
      });
      setIsEditingGST(true);
      return;
    }
    setActiveEditFields((prev) => prev.filter((f) => f !== field));
  };

  const handleCancelEditField = (field: string) => {
    if (field === "GST") {
      const originalGST = (organizations[0]?.gst_number || "")
        .trim()
        .toUpperCase();

      formik.setFieldValue("gstNumber", originalGST);
      setSearchedGST("");
      setIsEditingGST(false);
      return;
    }

    if (field === "email") {
      formik.setFieldValue("email", email);
    }

    setActiveEditFields((prev) => prev.filter((f) => f !== field));
  };

  const handleEmailSave = () => {
    // Validate email field
    formik.setFieldTouched("email", true);

    const emailValue = formik.values.email;

    // Check if email is empty
    if (!emailValue || emailValue.trim() === "") {
      formik.setFieldError("email", "Email is required");
      toast.error("Email cannot be empty");
      return;
    }

    // Check if email format is valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue)) {
      formik.setFieldError("email", "Invalid email format");
      toast.error("Please enter a valid email address");
      return;
    }

    // If validation passes, close the edit mode and show success
    handleEditField("email");
    toast.success("Email updated successfully!");
  };

  const fetchGST = async () => {
    // Mark GST field as touched to show validation errors

    formik.setFieldTouched("gstNumber", true);

    const gstValue = formik.values.gstNumber;

    // Validate GST field
    if (!gstValue || gstValue.trim() === "") {
      formik.setFieldError("gstNumber", "GST Number is required");
      toast.error("Please enter GST Number");
      return;
    }

    // Check if GST is alphanumeric
    const alphanumericRegex = /^[a-zA-Z0-9]*$/;
    if (!alphanumericRegex.test(gstValue)) {
      formik.setFieldError("gstNumber", "GST number must be alphanumeric");
      toast.error("GST number must be alphanumeric");
      return;
    }

    // Check max length
    if (gstValue.length > 15) {
      formik.setFieldError(
        "gstNumber",
        "GST number must not exceed 15 characters",
      );
      toast.error("GST number must not exceed 15 characters");
      return;
    }

    const validGST = selectedVals("GST") || "";
    if (!isGSTValid(validGST)) return;

    try {
      const response = await dispatch(
        fetchGSTDetailsThunk({ gst_number: formik.values.gstNumber }),
      ).unwrap();

      setSearchedGST(
        (response?.gst_number || formik.values.gstNumber).trim().toUpperCase(),
      );

      toast.success("GST Details Fetched Successfully!");
      setInitialFormValues({
        ...formik.values,
        gstNumber: response?.gst_number || "",
        email: response?.email || "",
        cin: response?.cin || "",
        tin: response?.tin || "",
        business_name: response?.business_name || "",
        entity_type: response?.entity_type || "",
        business_nature: response?.nature_of_buisness || "",
        address: response?.orgAddress?.[0]?.address || "",
        city_name: response?.orgAddress?.[0]?.city_name || "",
        district_name: response?.district_name || "",
        state_name: response?.orgAddress?.[0]?.state_name || "",
        pincode: response?.orgAddress?.[0]?.pincode || "",
        registration_date:
          response?.registration_date || response?.registeration_number || "",
      });
      setIsEditingGST(false);
    } catch (error: any) {
      toast.error(error || error?.message || "Failed to fetch GST Details");
    }
  };

  const handleSendEmailOTP = async (email: string) => {
    try {
      const response = await dispatch(sendEmailOtpThunk({ email })).unwrap();
      if (response.payload?.success) {
        toast.success("OTP Sent Successfully to Email Address");
      }
      return response;
    } catch (error: any) {
      toast.error(error?.message || "Something Went Wrong!");
      return { success: false };
    }
  };

  const handleVerifyEmailOTP = async (otp: string) => {
    try {
      const response = await dispatch(
        verifyEmailOtpThunk({ email: emailAddress, otp_code: otp }),
      ).unwrap();

      if (response?.success) {
        toast.success("OTP Verified Successfully!");
        dispatch(fetchProfile());
      }

      return response;
    } catch (error: any) {
      toast.error(error?.message || "Something Went Wrong!");
      return { success: false };
    }
  };

  const handleClear = () => {
    const isConfirmed = window.confirm(
      "This action will remove the GST details from the system. Are you sure you wish to proceed?",
    );

    if (isConfirmed) {
      handleDeleteField("GST");
    }
  };

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const gstRegex = /^[0-9]{2}[A-Z0-9]{13}$/;

  const isGSTValid = (gst: string) => gstRegex.test(gst);

  React.useEffect(() => {
    user_category && formik.setFieldValue("userCategory", user_category);
    if (organizations && organizations[0]?.gst_number) {
      // handleEditField("GST");
      setIsEditingGST(false);

      setInitialFormValues({
        ...formik.values,
        gstNumber:
          organizations && organizations.length > 0
            ? organizations[0].gst_number
            : "",
        userCategory: user_category || 0,
        email: email,
        cin:
          organizations && organizations.length > 0 ? organizations[0].cin : "",
        tin:
          organizations && organizations.length > 0 ? organizations[0].tin : "",
        business_name:
          organizations && organizations.length > 0
            ? organizations[0].business_name
            : "",
        entity_type:
          organizations && organizations.length > 0
            ? organizations[0].entity_type
            : "",
        business_nature:
          organizations && organizations.length > 0
            ? organizations[0].business_nature
            : "",
        address: "",
        city_name:
          organizations && organizations.length > 0
            ? organizations[0].city_name
            : "",
        district_name:
          organizations && organizations.length > 0
            ? organizations[0].district_name
            : "",
        state_name:
          organizations && organizations.length > 0
            ? organizations[0].state_name
            : "",
        pincode:
          organizations && organizations.length > 0
            ? organizations[0].pincode
            : "",
        registration_date:
          organizations && organizations.length > 0
            ? organizations[0].registration_date
            : "",
      });
    }
    if (organizations && !organizations[0]?.gst_number)
      handleDeleteField("GST");
  }, [organizations]);

  const [initialFormValues, setInitialFormValues] = useState({
    gstNumber: "",
    userCategory: 0,
    email: "",
    cin: "",
    tin: "",
    business_name: "",
    entity_type: "",
    business_nature: "",
    address: "",
    city_name: "",
    district_name: "",
    state_name: "",
    pincode: "",
    registration_date: "",
  });

  const [emailVerifyModal, setEmailVerifyModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema: SubmitValidationSchema,
    onSubmit: async (values) => {
      const submitDetailsPayload = {
        gst_number: values?.gstNumber || "",
        email: values?.email || "",
        cin: values?.cin || "",
        tin: values?.tin || "",
        business_name: values?.business_name || "",
        entity_type: values?.entity_type || "",
        business_nature: values?.business_nature || "",
        district_name: values?.district_name || "",
        pincode: Number(values?.pincode || 0),
        state_name: values?.state_name || "",
        city_name: values?.city_name || "",
        registration_date: values?.registration_date || "",
        user_category: Number(values?.userCategory || 0),
      };

      console.log("submitDetailsPayload", submitDetailsPayload);
      try {
        const response: any = await dispatch(
          submitUserRegistrationDetailsThunk({ submitDetailsPayload, id }),
        ).unwrap();

        if (response) {
          toast.success("Profile Updated Successfully!");
          // navigate("/application-dashboard");
        } else {
          toast.error(response?.message || "Something Went Wrong!");
        }
      } catch (error: any) {
        toast.error(error || "Something Went Wrong!");
      }
    },
  });
  useEffect(() => {
    dispatch(fetchProfile());
    // handleEditField("GST");
    if (organizations && organizations?.[0]?.gst_number) {
      setIsEditingGST(false);
    } else {
      setIsEditingGST(true);
    }
  }, [dispatch]);

  const hasGST = Boolean(formik?.values?.gstNumber);

  const handleFieldChange = (field: string, value: string) => {
    if (field === "GST") {
      // Only allow alphanumeric characters and max 15 characters
      const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, "");
      const truncatedValue = alphanumericValue.slice(0, 15);

      setSearchedGST("");
      formik.setFieldValue("gstNumber", truncatedValue);
    } else if (field === "email") {
      formik.setFieldValue("email", value);
    } else if (field === "CIN") {
      formik.setFieldValue("cin", value);
    } else if (field === "TIN") {
      formik.setFieldValue("tin", value);
    }
  };

  const renderClickToAdd = (field: string) => {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <StyledTextField
            type="text"
            placeholder={`Enter ${field}`}
            value={selectedVals(field)}
            onChange={(e) => {
              handleFieldChange(field, e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
            inputProps={{
              maxLength: field === "GST" ? 15 : undefined,
            }}
            InputProps={{
              sx: { height: 40 },
            }}
            style={{
              width: 280,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: "transparent",
              borderRadius: 6,
              fontSize: 14,
              outline: "none",
              boxSizing: "border-box",
              color: "#878787",
            }}
          />
          {/* <Button
            variant="outlined"
            sx={{
              borderRadius: "24px",
              width: 46,
              height: 30,
              fontWeight: 500,
              color: "#3A1C73",
              borderColor: "#B0B7C3",
              fontSize: "12px",
              background: "#fff",
              textTransform: "none",
              px: 0,
              py: 0,
              paddingTop: "10px",
              paddingRight: "24px",
              paddingBottom: "10px",
              paddingLeft: "24px",
              gap: "8px",
              boxShadow: "none",
              "&:hover": {
                background: "#F6F3FB",
                borderColor: "#3A1C73"
              }
            }}
            onClick={() => handleEditField(field)}
          >
            Cancel
          </Button> */}
        </Box>
      </>
    );
  };

  const formatDateString = (dateStr?: string) => {
    if (!dateStr) return dateStr;

    if (dayjs(dateStr, "DD/MM/YYYY", true).isValid()) {
      return dateStr;
    }

    if (/^\d{8}$/.test(dateStr)) {
      return `${dateStr.slice(0, 2)}/${dateStr.slice(2, 4)}/${dateStr.slice(
        4,
      )}`;
    }

    if (dayjs(dateStr).isValid()) {
      return dayjs(dateStr).format("DD/MM/YYYY");
    }

    return dateStr; // fallback
  };

  useEffect(() => {
    if (isValidEmail(email)) {
      setEmailAddress(email);
    }
  }, [formik.values.email, dispatch]);

  const gstValue = (formik.values.gstNumber || "").trim().toUpperCase();
  const currentCategory = Number(formik.values.userCategory);
  const originalGST = (organizations?.[0]?.gst_number || "")
    .trim()
    .toUpperCase();
  const originalCategory = Number(user_category);

  const gstIsValid = gstRegex.test(gstValue);

  const isExistingGSTUnchanged =
    Boolean(originalGST) &&
    gstValue === originalGST &&
    currentCategory === originalCategory;

  const isGSTAllowedForSubmit = !gstValue
    ? currentCategory === 1
    : gstIsValid && (gstValue === originalGST || searchedGST === gstValue);

  return (
    <Box className="dashboard-container">
      <div className="page-content">
        {/* <DashboardHeader /> */}
        <GenericBreadCrums breadcrumbs={breadcrumbData} className="mt-6" />
        <Box className="dashboard-title-box">
          <Box sx={{ mt: 2 }}>
            {/* Profile Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Box
                sx={{
                  width: 35,
                  height: 35,
                  borderRadius: "50%",
                  background: "#E7E8E9",
                  border: "1px solid #3A1C73",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  fontSize: 12,
                  color: "#3A1C73",
                }}
              >
                {getFirstAndLastInitial(name)}
              </Box>
              <Typography
                variant="h5"
                fontWeight={700}
                fontSize={14}
                color="#000000"
              >
                {name}
              </Typography>
            </Box>
            {/* Important Note */}
            <Box
              sx={{
                mb: 2,
                px: 2,
                py: 1,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: 1.25,
                border: "1px solid #FFE7BA",
                borderRadius: "8px",
                backgroundColor: "#FFFAF0",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  pt: "2px",
                  flexShrink: 0,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 18,
                    height: 18,
                    border: "1.5px solid #CC8801",
                    borderRadius: "50%",
                    color: "#CC8801",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  i
                </Box>
              </Box>

              <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#CC8801",
                    lineHeight: 1.5,
                    mb: 0.25,
                  }}
                >
                  Important Note:
                </Typography>

                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: "#212121",
                    lineHeight: 1.5,
                  }}
                >
                  The profile and organization details provided here will be
                  used throughout the Rajmarg Pravesh Portal for application
                  processing, official document generation, notifications,
                  payment processing, and other related activities. Please
                  ensure that all information is accurate, complete, and up to
                  date before proceeding.
                </Typography>
              </Box>
            </Box>

            <form action="submit" onSubmit={formik.handleSubmit}>
              {/* Basic Details Card */}
              <Card
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  boxShadow: "0px 8px 16px -8px rgba(58, 28, 115, 0.12)",
                  p: 3,
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ mb: 3, textAlign: "left" }}
                  >
                    Basic Details
                  </Typography>
                  <Grid container spacing={0}>
                    <Grid item xs={12} md={3}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 1,
                        }}
                      >
                        <span>
                          <CalendarMonthIcon
                            sx={{ fontSize: 28, color: "#212121" }}
                          />
                        </span>
                        <Box>
                          <Typography
                            fontWeight={500}
                            color="#757575"
                            fontSize={16}
                            sx={{ textAlign: "left" }}
                          >
                            Date of Birth
                          </Typography>
                          <Typography
                            fontWeight={500}
                            fontSize={14}
                            sx={{ textAlign: "left" }}
                            color="#212121"
                          >
                            {formatDateString(dob)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 1,
                        }}
                      >
                        <span>
                          <CallIcon sx={{ fontSize: 28, color: "#212121" }} />
                        </span>
                        <Box>
                          <Typography
                            fontWeight={500}
                            color="#757575"
                            fontSize={16}
                            sx={{ textAlign: "left" }}
                          >
                            Mobile Number
                          </Typography>
                          <Typography
                            fontWeight={500}
                            fontSize={14}
                            sx={{ textAlign: "left" }}
                            color="#212121"
                          >
                            {mobile_number}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 1,
                        }}
                      >
                        <span>
                          <MailOutlineIcon
                            sx={{ fontSize: 28, color: "#212121" }}
                          />
                        </span>
                        <Box>
                          <Typography
                            fontWeight={500}
                            color="#757575"
                            fontSize={16}
                            sx={{
                              textAlign: "left",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            Email <span style={{ color: "#B3261D" }}>*</span>
                            <span onClick={() => setOpen(true)}>
                              <EditIcon
                                sx={{
                                  fontSize: 16,
                                  color: "#3A1C73",
                                  cursor: "pointer",
                                }}
                              />
                            </span>
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 0,
                            }}
                          >
                            {activeEditFields.includes("email") ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <StyledTextField
                                    type="text"
                                    placeholder="Enter email"
                                    value={formik.values.email}
                                    onChange={(e) =>
                                      formik.setFieldValue(
                                        "email",
                                        e.target.value,
                                      )
                                    }
                                    InputProps={{ sx: { height: 40 } }}
                                    error={
                                      formik.touched.email &&
                                      Boolean(formik.errors.email)
                                    }
                                    helperText={
                                      formik.touched.email &&
                                      formik.errors.email
                                    }
                                    style={{
                                      width: 250,
                                      borderWidth: 1,
                                      borderStyle: "solid",
                                      borderColor: "transparent",
                                      borderRadius: 6,
                                      fontSize: 14,
                                      outline: "none",
                                      boxSizing: "border-box",
                                      color: "#878787",
                                    }}
                                  />
                                  <Button
                                    variant="outlined"
                                    sx={{
                                      borderRadius: "50px",
                                      border: "1px solid #B0B7C3",
                                      background: "#fff",
                                      color: "#3A1C73",
                                      fontWeight: 500,
                                      fontSize: "14px",
                                      width: "100px",
                                      height: "40px",
                                      textTransform: "none",
                                      boxShadow: "none",
                                      "&:hover": {
                                        background: "#F6F3FB",
                                        borderColor: "#3A1C73",
                                      },
                                    }}
                                    onClick={handleEmailSave}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    sx={{
                                      borderRadius: "50px",
                                      border: "1px solid #B0B7C3",
                                      background: "#fff",
                                      color: "#3A1C73",
                                      fontWeight: 500,
                                      fontSize: "14px",
                                      width: "100px",
                                      height: "40px",
                                      textTransform: "none",
                                      boxShadow: "none",
                                      "&:hover": {
                                        background: "#F6F3FB",
                                        borderColor: "#3A1C73",
                                      },
                                    }}
                                    onClick={() =>
                                      handleCancelEditField("email")
                                    }
                                  >
                                    Cancel
                                  </Button>
                                </Box>
                                {/* {formik.touched.email &&
                                  formik.errors.email && (
                                    <Typography
                                      variant="caption"
                                      color="error"
                                      sx={{ ml: 1 }}
                                    >
                                      {formik.errors.email}
                                    </Typography>
                                  )} */}
                              </Box>
                            ) : (
                              <>
                                <Typography
                                  fontWeight={500}
                                  fontSize={14}
                                  sx={{
                                    textAlign: "left",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                  color="#212121"
                                >
                                  {email}
                                </Typography>
                                {email && isValidEmail(email) && (
                                  <Typography>
                                    <Verified
                                      sx={{
                                        color: "#34A853",
                                        marginTop: "1px",
                                      }}
                                    />
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      <EmailVerificationModal
                        open={open}
                        email={emailAddress}
                        onClose={() => setOpen(false)}
                        onEmailChange={(value) => setEmailAddress(value)}
                        onSendOtp={() => handleSendEmailOTP(emailAddress)}
                        onVerifyOtp={(otp) => handleVerifyEmailOTP(otp)}
                      />
                    </Grid>
                    <Grid item xs={12} style={{ height: 24 }} />
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <span>
                          <BusinessIcon
                            sx={{ fontSize: 28, color: "#212121" }}
                          />
                        </span>
                        <Box>
                          <Typography
                            fontWeight={500}
                            color="#757575"
                            fontSize={16}
                            sx={{ textAlign: "left" }}
                          >
                            Address
                          </Typography>
                          <Typography
                            fontWeight={500}
                            fontSize={14}
                            sx={{ textAlign: "left", pr: 3 }}
                            color="#212121"
                          >
                            {address}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    {/* <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 1
                        }}
                      >
                        <span>
                          <BadgeIcon sx={{ fontSize: 28, color: "#212121" }} />
                        </span>
                        <Box>
                          <Typography
                            fontWeight={500}
                            color="#757575"
                            fontSize={16}
                            sx={{ textAlign: "left" }}
                          >
                            Aadhar Card No:
                          </Typography>
                          <Typography
                            fontWeight={500}
                            fontSize={14}
                            sx={{ textAlign: "left" }}
                            color="#212121"
                          >
                            6632 **** 9128
                          </Typography>
                        </Box>
                      </Box>
                    </Grid> */}
                  </Grid>
                </CardContent>
              </Card>
              {/* Organisation Details Card */}
              <Card
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  boxShadow: "0px 8px 16px -8px rgba(58, 28, 115, 0.12)",
                  p: 3,
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ mb: 3, textAlign: "left" }}
                  >
                    Organisation Details
                  </Typography>
                  <Grid container spacing={0} alignItems="center">
                    {/* Category Selection */}
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        {/* Icon */}
                        <Box sx={{ mr: 2 }}>
                          <CategoryIcon
                            sx={{ fontSize: 28, color: "#212121" }}
                          />
                        </Box>
                        <Box>
                          <FormControl
                            sx={{ mb: 2 }}
                            error={Boolean(
                              formik.touched.userCategory &&
                              formik.errors.userCategory,
                            )}
                          >
                            <FormLabel>
                              <Typography
                                fontWeight={500}
                                color="#757575"
                                fontSize={16}
                                sx={{ mb: 1, textAlign: "left" }}
                              >
                                Select Your Category
                                <span style={{ color: "#B3261D" }}>*</span>
                              </Typography>
                            </FormLabel>
                            <RadioGroup
                              row
                              name="userCategory"
                              value={formik.values.userCategory}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "userCategory",
                                  Number(e.target.value),
                                )
                              }
                              onBlur={formik.handleBlur}
                            >
                              <FormControlLabel
                                value={1}
                                control={<Radio sx={{ color: "#757575" }} />}
                                label={
                                  <Typography
                                    fontWeight={500}
                                    fontSize={14}
                                    color="#212121"
                                  >
                                    Individual
                                  </Typography>
                                }
                              />
                              <FormControlLabel
                                value={2}
                                control={<Radio sx={{ color: "#757575" }} />}
                                label={
                                  <Typography
                                    fontWeight={500}
                                    fontSize={14}
                                    color="#212121"
                                  >
                                    Organisation
                                  </Typography>
                                }
                              />
                            </RadioGroup>
                            {formik.touched.userCategory &&
                              formik.errors.userCategory && (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{
                                    ml: 1,
                                    mt: 0.5,
                                    display: "block",
                                    textAlign: "left",
                                  }}
                                >
                                  {formik.errors.userCategory}
                                </Typography>
                              )}
                          </FormControl>
                        </Box>
                      </Box>
                    </Grid>
                    {/* GST Number Input */}
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        {/* Icon */}
                        <Box sx={{ mr: 2 }}>
                          <ShoppingBagIcon
                            sx={{ fontSize: 28, color: "#212121" }}
                          />
                        </Box>
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            fontWeight={500}
                            color="#757575"
                            fontSize={16}
                            sx={{ mb: 1, textAlign: "left" }}
                          >
                            GST Number{" "}
                            {Number(formik.values.userCategory) === 1 && (
                              <span
                                style={{ color: "#878787", fontSize: "12px" }}
                              >
                                (Optional)
                              </span>
                            )}
                            {Number(formik.values.userCategory) === 2 && (
                              <span style={{ color: "#B3261D" }}>*</span>
                            )}
                          </Typography>
                          {!isEditingGST ? (
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  fontWeight={500}
                                  fontSize={14}
                                  color="#212121"
                                  sx={{ mb: 0, textAlign: "left" }}
                                >
                                  {formik?.values?.gstNumber || ""}
                                </Typography>
                                <EditIcon
                                  sx={{
                                    fontSize: 20,
                                    color: "#3A1C73",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => setIsEditingGST(true)}
                                />

                                <DeleteIcon
                                  sx={{
                                    width: 20,
                                    color: "#8f1e17",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                  }}
                                  onClick={handleClear}
                                />

                                {/* <Typography
                                  fontWeight={500}
                                  fontSize={16}
                                  color="#3A1C73"
                                  sx={{ cursor: "pointer" }}
                                  onClick={() => handleEditField("GST")}
                                >
                                  {formik?.values?.gstNumber
                                    ? "Edit"
                                    : "Click to add"}
                                </Typography> */}
                              </Box>
                            </>
                          ) : (
                            <>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: 2,
                                }}
                              >
                                {renderClickToAdd("GST")}
                                {/* <Button
                                  variant="outlined"
                                  onClick={() => fetchGST()}
                                  sx={{
                                    borderRadius: "24px",
                                    width: 56,
                                    height: 30,
                                    fontWeight: 500,
                                    color: "#3A1C73",
                                    borderColor: "#B0B7C3",
                                    fontSize: "14px",
                                    background: "#fff",
                                    textTransform: "none",
                                    px: 0,
                                    py: 0,
                                    paddingTop: "10px",
                                    paddingRight: "24px",
                                    paddingBottom: "10px",
                                    paddingLeft: "24px",
                                    gap: "8px",
                                    boxShadow: "none",
                                    "&:hover": {
                                      background: "#F6F3FB",
                                      borderColor: "#3A1C73"
                                    }
                                  }}
                                  // disabled={!gstNumber}
                                  // onClick={handleAddGst}
                                >
                                  Search
                                </Button> */}
                                <Button
                                  variant="outlined"
                                  onClick={fetchGST}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      fetchGST();
                                    }
                                  }}
                                  disabled={
                                    !isGSTValid(selectedVals("GST") || "")
                                  }
                                  sx={{
                                    borderRadius: "50px",
                                    border: "1px solid #B0B7C3",
                                    background: "#fff",
                                    color: "#3A1C73",
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    width: "100px",
                                    height: "40px",
                                    textTransform: "none",
                                    boxShadow: "none",
                                    "&:hover": {
                                      background: "#F6F3FB",
                                      borderColor: "#3A1C73",
                                    },
                                  }}
                                >
                                  Search
                                </Button>
                                {formik.values.gstNumber &&
                                  organizations?.[0]?.gst_number && (
                                    <Button
                                      variant="outlined"
                                      sx={{
                                        borderRadius: "50px",
                                        border: "1px solid #B0B7C3",
                                        background: "#fff",
                                        color: "#3A1C73",
                                        fontWeight: 500,
                                        fontSize: "14px",
                                        width: "100px",
                                        height: "40px",
                                        textTransform: "none",
                                        boxShadow: "none",
                                        "&:hover": {
                                          background: "#F6F3FB",
                                          borderColor: "#3A1C73",
                                        },
                                      }}
                                      onClick={() =>
                                        handleCancelEditField("GST")
                                      }
                                    >
                                      Cancel
                                    </Button>
                                  )}
                              </Box>
                            </>
                          )}
                          {formik.touched.gstNumber &&
                            formik.errors.gstNumber && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{
                                  ml: 1,
                                  mt: 0.5,
                                  display: "block",
                                  textAlign: "left",
                                }}
                              >
                                {formik.errors.gstNumber}
                              </Typography>
                            )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  {/* Organisation Info - show after GST is added */}
                  {formik.values?.business_name?.length > 0 && (
                    <Box
                      sx={{
                        mt: 3,
                        border: "1px solid #E7E8E9",
                        borderRadius: 2,
                        background: "#fff",
                        p: 2,
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <ApartmentIcon
                              sx={{ fontSize: 24, color: "#212121" }}
                            />
                            <Box>
                              <Typography
                                fontWeight={500}
                                color="#757575"
                                fontSize={14}
                                sx={{ textAlign: "left" }}
                              >
                                Business Name / Trade Name
                              </Typography>
                              <Typography
                                fontWeight={500}
                                fontSize={14}
                                color="#212121"
                                sx={{ textAlign: "left" }}
                              >
                                {formik?.values?.business_name || ""}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        {formik?.values?.entity_type && (
                          <Grid item xs={12} md={4}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Groups3Icon
                                sx={{ fontSize: 24, color: "#212121" }}
                              />
                              <Box>
                                <Typography
                                  fontWeight={500}
                                  color="#757575"
                                  fontSize={14}
                                  sx={{ textAlign: "left" }}
                                >
                                  Entity Type
                                </Typography>
                                <Typography
                                  textAlign="left"
                                  fontWeight={500}
                                  fontSize={14}
                                  color="#212121"
                                >
                                  {formik?.values?.entity_type || ""}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        )}
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <CorporateFareIcon
                              sx={{ fontSize: 24, color: "#212121" }}
                            />
                            <Box>
                              <Typography
                                fontWeight={500}
                                color="#757575"
                                fontSize={14}
                                sx={{ textAlign: "left" }}
                              >
                                Nature of Business
                              </Typography>
                              <Typography
                                fontWeight={500}
                                fontSize={14}
                                sx={{ textAlign: "left" }}
                                color="#212121"
                              >
                                {formik?.values?.business_nature || ""}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <HomeWorkIcon
                              sx={{ fontSize: 24, color: "#212121" }}
                            />
                            <Box>
                              <Typography
                                fontWeight={500}
                                color="#757575"
                                fontSize={14}
                                sx={{ textAlign: "left" }}
                              >
                                Address
                              </Typography>
                              <Typography
                                fontWeight={500}
                                fontSize={14}
                                color="#212121"
                                sx={{ textAlign: "left" }}
                              >
                                {`${formik?.values?.city_name || ""} ${
                                  formik?.values?.district_name || ""
                                }`}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <DomainIcon
                              sx={{ fontSize: 24, color: "#212121" }}
                            />
                            <Box>
                              <Typography
                                fontWeight={500}
                                color="#757575"
                                fontSize={14}
                                sx={{ textAlign: "left" }}
                              >
                                City
                              </Typography>
                              <Typography
                                fontWeight={500}
                                fontSize={14}
                                color="#212121"
                              >
                                {formik?.values?.city_name || ""}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <LocationCityIcon
                              sx={{ fontSize: 24, color: "#212121" }}
                            />
                            <Box>
                              <Typography
                                fontWeight={500}
                                color="#757575"
                                fontSize={14}
                                sx={{ textAlign: "left" }}
                              >
                                State
                              </Typography>
                              <Typography
                                fontWeight={500}
                                fontSize={14}
                                color="#212121"
                              >
                                {formik?.values?.state_name || ""}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <LocationOnIcon
                              sx={{ fontSize: 24, color: "#212121" }}
                            />
                            <Box>
                              <Typography
                                fontWeight={500}
                                color="#757575"
                                fontSize={14}
                                sx={{ textAlign: "left" }}
                              >
                                Pincode
                              </Typography>
                              <Typography
                                fontWeight={500}
                                fontSize={14}
                                color="#212121"
                              >
                                {formik?.values?.pincode || ""}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <EventRepeatIcon
                              sx={{ fontSize: 24, color: "#212121" }}
                            />
                            <Box>
                              <Typography
                                fontWeight={500}
                                color="#757575"
                                fontSize={14}
                                sx={{ textAlign: "left" }}
                              >
                                Registration Date
                              </Typography>
                              <Typography
                                fontWeight={500}
                                fontSize={14}
                                color="#212121"
                                sx={{ textAlign: "left" }}
                              >
                                {formik?.values?.registration_date || ""}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "start",
                              gap: 2,
                            }}
                          >
                            <KeyboardIcon
                              sx={{ fontSize: 24, color: "#212121" }}
                            />
                            <Box>
                              <Typography
                                fontWeight={500}
                                color="#757575"
                                fontSize={14}
                                sx={{
                                  textAlign: "left",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                TIN
                                <EditIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "#3A1C73",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleEditField("TIN")}
                                />
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {activeEditFields.includes("TIN") ? (
                                  renderClickToAdd("TIN")
                                ) : (
                                  <>
                                    <Typography
                                      fontWeight={500}
                                      fontSize={14}
                                      sx={{ textAlign: "left" }}
                                      color="#212121"
                                    >
                                      {formik.values?.tin || ""}
                                    </Typography>

                                    {/* <Typography
                                      fontWeight={500}
                                      fontSize={16}
                                      color="#3A1C73"
                                      sx={{ cursor: "pointer" }}
                                      onClick={() => handleEditField("TIN")}
                                    >
                                      {formik?.values?.tin
                                        ? "Edit"
                                        : "Click to add"}
                                    </Typography> */}
                                  </>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "start",
                              gap: 2,
                            }}
                          >
                            <VpnKeyIcon
                              sx={{ fontSize: 24, color: "#212121" }}
                            />
                            <Box>
                              <Typography
                                fontWeight={500}
                                color="#757575"
                                fontSize={14}
                                sx={{
                                  textAlign: "left",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                CIN
                                <EditIcon
                                  sx={{
                                    fontSize: 16,
                                    color: "#3A1C73",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleEditField("CIN")}
                                />
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {activeEditFields.includes("CIN") ? (
                                  renderClickToAdd("CIN")
                                ) : (
                                  <>
                                    <Typography
                                      fontWeight={500}
                                      fontSize={14}
                                      sx={{ textAlign: "left" }}
                                      color="#212121"
                                    >
                                      {formik.values?.cin || ""}
                                    </Typography>

                                    {/* <Typography
                                      fontWeight={500}
                                      fontSize={16}
                                      color="#3A1C73"
                                      sx={{ cursor: "pointer" }}
                                      onClick={() => handleEditField("CIN")}
                                    >
                                      {formik?.values?.cin
                                        ? "Edit"
                                        : "Click to add"}
                                    </Typography> */}
                                  </>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Continue Button */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}
              >
                <Button
                  variant="contained"
                  disabled={!formik.values.userCategory}
                  onClick={() => navigate("/application-dashboard")}
                  sx={{
                    borderRadius: "24px",
                    background: "#fff",
                    border: "1px solid #3A1C73",
                    boxShadow: "none",
                    color: "#3A1C73",
                    fontWeight: 600,
                    fontSize: "14px",
                    px: 6,
                    py: 1.5,
                    textTransform: "none",
                    "&:disabled": {
                      background: "#B0B7C3",
                      color: "#fff",
                      opacity: 0.6,
                    },
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={
                    !formik.isValid ||
                    !isGSTAllowedForSubmit ||
                    formik.isSubmitting
                  }
                  sx={{
                    borderRadius: "24px",
                    background: "#3A1C73",
                    boxShadow: "none",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "14px",
                    px: 6,
                    py: 1.5,
                    textTransform: "none",
                    "&:disabled": {
                      background: "#B0B7C3",
                      color: "#fff",
                      opacity: 0.6,
                    },
                  }}
                >
                  Submit
                </Button>
              </Box>
            </form>
            <br />
            <br />
          </Box>
          <br />
          <br />
        </Box>
      </div>
    </Box>
  );
};

export default ViewProfile;
